import React from 'react';

/**
 * Container - Provides consistent max-width and padding for content
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to wrap
 * @param {string} [props.as] - HTML element to render as (default: 'div')
 * @param {string} [props.size] - Container size variant
 * @param {boolean} [props.centered] - Center content horizontally
 * @param {string} [props.padding] - Padding variant
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref
 */
export const Container = React.forwardRef(function Container({
  children,
  as: Component = 'div',
  size = 'default',
  centered = true,
  padding = 'default',
  className = ''
}, ref) {
  // Size variants for max-width
  const sizeClasses = {
    sm: 'max-w-3xl',      // 768px
    default: 'max-w-7xl', // 1280px
    lg: 'max-w-screen-2xl', // 1536px
    full: 'max-w-full',
    prose: 'max-w-prose'  // ~65ch for reading
  };

  // Padding variants
  const paddingClasses = {
    none: '',
    sm: 'px-4 sm:px-6',
    default: 'px-4 sm:px-6 lg:px-8',
    lg: 'px-6 sm:px-8 lg:px-12',
    y: 'py-8',
    xy: 'px-4 sm:px-6 lg:px-8 py-8'
  };

  const containerClasses = [
    sizeClasses[size] || sizeClasses.default,
    paddingClasses[padding] || paddingClasses.default,
    centered && 'mx-auto',
    className
  ].filter(Boolean).join(' ');

  return (
    <Component ref={ref} className={containerClasses}>
      {children}
    </Component>
  );
});

/**
 * PageContainer - Full page container with min-height
 * @param {Object} props - Same as Container plus:
 * @param {string} [props.background] - Background color variant
 */
export function PageContainer({ 
  children,
  background = 'base-100',
  className = '',
  ...containerProps 
}) {
  const bgClasses = {
    'base-100': 'bg-base-100',
    'base-200': 'bg-base-200',
    'base-300': 'bg-base-300',
    transparent: ''
  };

  return (
    <div className={`min-h-screen ${bgClasses[background] || bgClasses['base-100']} ${className}`}>
      <Container {...containerProps}>
        {children}
      </Container>
    </div>
  );
}

/**
 * Section - Container with vertical spacing for page sections
 * @param {Object} props - Same as Container plus:
 * @param {string} [props.spacing] - Vertical spacing variant
 */
export function Section({ 
  children,
  spacing = 'default',
  className = '',
  ...containerProps 
}) {
  const spacingClasses = {
    none: '',
    sm: 'py-8',
    default: 'py-12 sm:py-16',
    lg: 'py-16 sm:py-24',
    xl: 'py-24 sm:py-32'
  };

  return (
    <Container 
      as="section"
      className={`${spacingClasses[spacing] || spacingClasses.default} ${className}`}
      {...containerProps}
    >
      {children}
    </Container>
  );
}

// Named exports
export default Container;